import React, { useState } from 'react';
import { Box, Button, Flex, Text, Textarea, useToast } from '@chakra-ui/react';
import baseService from '../../services/base';
import useDebouncedSave from '../../hooks/useDebouncedSave';
import SaveStatusHint from '../Feedback/SaveStatusHint';
import { AUDIT, AUDIT_LABEL } from '../../theme/auditTokens';
import { nomeDoArquivo } from '../../utils/nome-de-upload';

// Comentário da recusa: mesmo auto-save da nota. Só grava com veredito — é o que
// o backend exige (o PATCH manda approved + comentário juntos).
function RejectComment({ fileId, approved, value, onChange, isDisabled }) {
 const save = useDebouncedSave(async (comment) => {
  await baseService.patch(`question-response/file/${fileId}/approval`, {
   approved,
   reviewer_comment: comment || null,
  });
 });

 return (
  <Box mt='8px'>
   <Textarea
    size='sm'
    rows={2}
    placeholder='Motivo da recusa (opcional)'
    value={value}
    onChange={(e) => {
     onChange(e.target.value);
     save.schedule(e.target.value);
    }}
    onBlur={() => save.flush()}
    isDisabled={isDisabled}
   />
   {!isDisabled && (
    <Flex justify='flex-end' mt='4px'>
     <SaveStatusHint status={save.status} onRetry={save.retry} />
    </Flex>
   )}
  </Box>
 );
}

// A PK da entity é `id_question_response_files` (plural), mas parte dos payloads
// serializa no singular — aceitar as duas evita depender de qual rota carregou.
function fileKey(file) {
 return file?.id_question_response_files ?? file?.id_question_response_file;
}

/**
 * Veredito do auditor por evidência (arquivo) da questão. Aprovar/recusar grava
 * na hora; o motivo da recusa salva sozinho enquanto o auditor escreve.
 */
export default function QuestionEvidenceVerdict({ files, isDisabled }) {
 const toast = useToast();

 const [approvals, setApprovals] = useState(() =>
  Object.fromEntries(files.map((f) => [fileKey(f), f.approved ?? null]))
 );
 const [comments, setComments] = useState(() =>
  Object.fromEntries(files.map((f) => [fileKey(f), f.reviewer_comment ?? '']))
 );
 const [savingId, setSavingId] = useState(null);

 async function setVerdict(fileId, approved) {
  setApprovals((prev) => ({ ...prev, [fileId]: approved }));
  setSavingId(fileId);
  try {
   await baseService.patch(`question-response/file/${fileId}/approval`, {
    approved,
    reviewer_comment: comments[fileId] || null,
   });
  } catch {
   toast({ title: 'Não foi possível salvar o veredito', status: 'error' });
  } finally {
   setSavingId(null);
  }
 }

 if (!files?.length) return null;

 return (
  <Box borderTop={`1px solid ${AUDIT.line}`} pt='14px' mt='14px'>
   <Text sx={AUDIT_LABEL} mb='10px'>
    Evidências
   </Text>

   {files.map((file) => {
    const fileId = fileKey(file);
    const approved = approvals[fileId];

    return (
     <Box key={fileId} mb='12px'>
      <Flex align='center' justify='space-between' gap='12px' wrap='wrap'>
       {/* EG-73: nome possivelmente gravado como latin-1 (multer sem
           defParamCharset). Decodificado na leitura — o dado no banco nao e
           tocado, por decisao do TL. */}
       <Text fontSize='sm' color={AUDIT.inkMuted} noOfLines={1} maxW='55%'>
        {nomeDoArquivo(file)}
       </Text>
       <Flex gap='8px'>
        <Button
         size='sm'
         colorScheme='green'
         variant={approved === true ? 'solid' : 'outline'}
         isLoading={savingId === fileId}
         isDisabled={isDisabled}
         onClick={() => setVerdict(fileId, true)}
        >
         Aceitar
        </Button>
        <Button
         size='sm'
         colorScheme='red'
         variant={approved === false ? 'solid' : 'outline'}
         isLoading={savingId === fileId}
         isDisabled={isDisabled}
         onClick={() => setVerdict(fileId, false)}
        >
         Recusar
        </Button>
       </Flex>
      </Flex>

      {approved === false && (
       <RejectComment
        fileId={fileId}
        approved={approved}
        value={comments[fileId] || ''}
        onChange={(value) => setComments((prev) => ({ ...prev, [fileId]: value }))}
        isDisabled={isDisabled}
       />
      )}
     </Box>
    );
   })}
  </Box>
 );
}
